import React, { useState, useCallback, memo, useEffect, useRef, useLayoutEffect, useMemo } from 'react'
import { useHistory } from 'react-router-dom'
import { PATH_URL } from 'src/helpers/path'
import { preserveUrlParams } from 'src/helpers/preserveUrlParams'
import styles from './styles.module.scss'
import { useTopCollections } from './useTopCollections.hook'
import ArrowIcon from 'src/assets/images/CollectionsQuizEntry/EssentialsCollectionPage/Arrow.svg'

type MappedImages = {
  top: string[]
  main: string
  small: string[]
  bottomLeft: string
  bottomRight: string
}

function mapImages(imagesObj: Record<string, string> | undefined): MappedImages {
  if (!imagesObj) return { top: [], main: '', small: [], bottomLeft: '', bottomRight: '' }
  const imageUrls = Object.values(imagesObj).filter(Boolean) as string[]
  // If there are less than 7 images, repeat randomly until there are 7 images
  while (imageUrls.length < 7) {
    if (imageUrls.length === 0) break
    const randomIdx = Math.floor(Math.random() * imageUrls.length)
    imageUrls.push(imageUrls[randomIdx])
  }
  return {
    top: imageUrls.slice(0, 2),
    main: imageUrls[2] || '',
    small: imageUrls.slice(3, 5),
    bottomLeft: imageUrls[5] || '',
    bottomRight: imageUrls[6] || '',
  }
}

/**
 * EssentialsCollectionPage Component
 * Displays collection options for users to choose from and add to their moodboard
 * @returns {JSX.Element} The EssentialsCollectionPage component
 */
const EssentialsCollectionPage: React.FC = () => {
  const history = useHistory()
  const { data: collections, loading, error } = useTopCollections()
  const [selectedCollection, setSelectedCollection] = useState<string>('')
  const [underlineStyle, setUnderlineStyle] = useState<{ left: number; width: number }>({ left: 0, width: 0 })
  const navRef = useRef<HTMLDivElement>(null)
  const [currentCarouselIndex, setCurrentCarouselIndex] = useState<number>(0)
  const carouselRef = useRef<HTMLDivElement>(null)
  const [touchStart, setTouchStart] = useState<number | null>(null)
  const [touchEnd, setTouchEnd] = useState<number | null>(null)

  // Check if data is from localStorage
  const isDataFromLocalStorage = useMemo(() => {
    const topCollectionsData = localStorage.getItem('topCollections')
    return topCollectionsData !== null && topCollectionsData !== '{}'
  }, [])

  // Helper to get collections array
  const collectionsArray = useMemo(() => {
    if (Array.isArray(collections)) return collections
    if (collections && typeof collections === 'object') return Object.values(collections)
    return []
  }, [collections])

  useEffect(() => {
    if (!collectionsArray.length || selectedCollection) return
    // Check if there is a collectionId from localStorage (from URL param)
    const collectionsQuizEntry = JSON.parse(localStorage.getItem('collectionsQuizEntry') || '{}')
    const savedCollectionId = collectionsQuizEntry.collectionId

    if (savedCollectionId) {
      // search by id, collectionId, name, slug
      const foundIndex = collectionsArray.findIndex(
        (col: any) =>
          col.id === savedCollectionId ||
          col.collectionId === savedCollectionId ||
          col.name === savedCollectionId ||
          col.slug === savedCollectionId
      )
      if (foundIndex !== -1) {
        const found = collectionsArray[foundIndex]
        setSelectedCollection(found.id || found.collectionId || found.name)
        setCurrentCarouselIndex(foundIndex)
        console.log('Auto-selected collection from URL parameter:', found.name, 'for ID:', savedCollectionId)
      } else {
        setSelectedCollection(collectionsArray[0].id || collectionsArray[0].collectionId || collectionsArray[0].name)
        setCurrentCarouselIndex(0)
        console.log(
          'Collection not found for ID:',
          savedCollectionId,
          'using first available:',
          collectionsArray[0].name
        )
      }
    } else {
      setSelectedCollection(collectionsArray[0].id || collectionsArray[0].collectionId || collectionsArray[0].name)
      setCurrentCarouselIndex(0)
    }
  }, [collectionsArray, selectedCollection])

  useLayoutEffect(() => {
    if (!navRef.current) return
    const active = navRef.current.querySelector(`.${styles.active}`) as HTMLElement
    if (active) {
      setUnderlineStyle({ left: active.offsetLeft, width: active.offsetWidth })
    }
  }, [selectedCollection, collectionsArray])

  // get collection
  const collection = useMemo(() => {
    if (!selectedCollection) return {}
    return (
      collectionsArray.find(
        (col: any) =>
          col.id === selectedCollection ||
          col.collectionId === selectedCollection ||
          col.name === selectedCollection ||
          col.slug === selectedCollection
      ) || {}
    )
  }, [collectionsArray, selectedCollection])

  const mappedImages = useMemo(() => mapImages(collection.images), [collection.images])

  const handleCollectionSelect = useCallback(
    (idOrName: string): void => {
      setSelectedCollection(idOrName)
      // Update carousel index when collection is selected
      const index = collectionsArray.findIndex(
        (col: any) =>
          col.id === idOrName || col.collectionId === idOrName || col.name === idOrName || col.slug === idOrName
      )
      if (index !== -1) {
        setCurrentCarouselIndex(index)
      }
    },
    [collectionsArray]
  )

  // Carousel navigation functions with circular navigation
  const handlePrevious = useCallback(() => {
    const newIndex = currentCarouselIndex === 0 ? collectionsArray.length - 1 : currentCarouselIndex - 1
    setCurrentCarouselIndex(newIndex)
    const collection = collectionsArray[newIndex]
    setSelectedCollection(collection.id || collection.collectionId || collection.name)
  }, [currentCarouselIndex, collectionsArray])

  const handleNext = useCallback(() => {
    const newIndex = currentCarouselIndex === collectionsArray.length - 1 ? 0 : currentCarouselIndex + 1
    setCurrentCarouselIndex(newIndex)
    const collection = collectionsArray[newIndex]
    setSelectedCollection(collection.id || collection.collectionId || collection.name)
  }, [currentCarouselIndex, collectionsArray])

  // Touch handlers for swipe
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null)
    setTouchStart(e.targetTouches[0].clientX)
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX)
  }

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return

    const distance = touchStart - touchEnd
    const isLeftSwipe = distance > 50
    const isRightSwipe = distance < -50

    if (isLeftSwipe) {
      handleNext()
    }
    if (isRightSwipe) {
      handlePrevious()
    }
  }

  const handleAddToMoodboard = useCallback((): void => {
    const collectionsQuizEntry = JSON.parse(localStorage.getItem('collectionsQuizEntry') || '{}')
    const collectionObj = collection
    collectionsQuizEntry.selectedCollection = {
      name: collectionObj?.name,
      id: collectionObj?.id || collectionObj?.collectionId || '',
    }
    localStorage.setItem('collectionsQuizEntry', JSON.stringify(collectionsQuizEntry))

    // Use preserveUrlParams to maintain query parameters
    const nextUrl = preserveUrlParams(PATH_URL.COLLECTIONS_QUIZ_PARTNER_FULL_NAME)
    history.push(nextUrl)
  }, [history, collection])

  if (loading || !selectedCollection) return null

  if (error) {
    return (
      <div className={styles.error}>
        <p>{error}</p>
        <button onClick={() => window.location.reload()}>Try Again</button>
      </div>
    )
  }

  return (
    <section className={styles.essentialsCollectionPage} aria-labelledby="collection-title">
      <div className={styles.essentialsCollectionPage_text}>
        <div className={styles.essentialsCollectionPage_contentLeft}>
          <header className={styles.essentialsCollectionPage__header__title}>
            <span id="collection-title" className={styles.essentialsCollectionPage__header__title__span}>
              COLLECTIONS DESIGNED TO INSPIRE
            </span>
          </header>

          <nav
            className={`${styles.essentialsCollectionPage__header__option} ${styles.hideOnMobile}`}
            role="tablist"
            ref={navRef}
            style={{ position: 'relative' }}
          >
            {collectionsArray.map((col: any) => (
              <span
                key={col.id || col.collectionId || col.name}
                role="tab"
                aria-selected={selectedCollection === (col.id || col.collectionId || col.name)}
                className={selectedCollection === (col.id || col.collectionId || col.name) ? styles.active : ''}
                onClick={() => handleCollectionSelect(col.id || col.collectionId || col.name)}
              >
                {col.name}
              </span>
            ))}
            <div
              className={styles.underline}
              style={{
                left: underlineStyle.left,
                width: underlineStyle.width,
                transition: 'left 0.3s cubic-bezier(.4,0,.2,1), width 0.3s cubic-bezier(.4,0,.2,1)',
                position: 'absolute',
                bottom: 0,
                height: 2,
                background: '#3E7C9D',
                borderRadius: 1,
                zIndex: 1,
              }}
            />
          </nav>

          <div className={styles.collection_info}>
            {/* <span className={styles.collection_info__price}>Starting at {collection.price}</span> */}
            <p>{collection.description}</p>
            <button
              onClick={handleAddToMoodboard}
              className={`${styles.collection_info__button} ${styles.hideOnMobile}`}
              aria-label="Add collection to moodboard"
            >
              shop flowers
            </button>
            <div className={styles.collection_info__explore}>
              <span>Not quite your style?</span>
              <a
                href="https://www.poppyflowers.com/poppy-collections"
                className={styles.explore_link}
                aria-label="View all collections"
              >
                Explore all collections
              </a>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.essentialsCollectionPage__imagesWrapper} role="presentation">
        <div className={styles.essentialsCollectionPage__images}>
          {/* Mobile Carousel */}
          <div className={styles.mobileCarousel}>
            <div
              className={styles.carouselWrapper}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              <div
                className={styles.carouselTrack}
                style={{ transform: `translateX(-${currentCarouselIndex * 100}%)` }}
              >
                {collectionsArray.map((col: any, index: number) => {
                  return (
                    <div key={col.id || col.collectionId || col.name || index} className={styles.carouselSlide}>
                      {col.vibeBoardMobileUrl && (
                        <img
                          src={col.vibeBoardMobileUrl}
                          alt={`${col.name} collection`}
                          className={styles.mobileVibeBoardImage}
                          loading="lazy"
                        />
                      )}
                    </div>
                  )
                })}
              </div>

              {/* Navigation Arrows - Always visible for circular navigation */}
              <button
                className={styles.carouselArrow + ' ' + styles.carouselArrowLeft}
                onClick={handlePrevious}
                aria-label="Previous collection"
              >
                <img src={ArrowIcon} alt="Previous" />
              </button>
              <button
                className={styles.carouselArrow + ' ' + styles.carouselArrowRight}
                onClick={handleNext}
                aria-label="Next collection"
              >
                <img src={ArrowIcon} alt="Next" />
              </button>
            </div>

            {/* Collection Title and Dots */}
            <div className={styles.carouselInfo}>
              <h2 className={styles.carouselTitle}>{collection.name}</h2>
              <div className={styles.carouselDots}>
                {collectionsArray.map((_, index) => (
                  <button
                    key={index}
                    className={`${styles.dot} ${index === currentCarouselIndex ? styles.dotActive : ''}`}
                    onClick={() => {
                      setCurrentCarouselIndex(index)
                      const col = collectionsArray[index]
                      setSelectedCollection(col.id || col.collectionId || col.name)
                    }}
                    aria-label={`Go to collection ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Desktop Grid */}
          <div className={styles.desktopGrid}>
            {collection.vibeBoardMobileUrl && (
              <img
                src={collection.vibeBoardMobileUrl}
                alt={`${collection.name} collection`}
                className={styles.vibeBoardImage}
                loading="lazy"
              />
            )}
          </div>

          {/* Mobile collection info - shown only on mobile after carousel */}
          <div className={styles.mobileCollectionInfo}>
            <p>{collection.description}</p>
            <div className={styles.collection_info__explore}>
              <span>Not quite your style?</span>
              <a
                href="https://www.poppyflowers.com/poppy-collections"
                className={styles.explore_link}
                aria-label="View all collections"
              >
                Explore all collections
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile sticky CTA button */}
      <button
        onClick={handleAddToMoodboard}
        className={styles.mobileCtaButton}
        aria-label="Add collection to moodboard"
      >
        shop flowers
      </button>
    </section>
  )
}

export default memo(EssentialsCollectionPage)
